import { useEffect, useState } from "react";
import {
  Autocomplete,
  TextField,
  Chip,
  Box,
  Checkbox,
  Typography,
  CircularProgress,
} from "@mui/material";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import { searchAvailableParties } from "../../services/ClientService";

const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function PartyAutocomplete({
  value,
  onChange,
  label = "Buscar partes por nombre, apellido o documento",
  excludeIds = [],
}) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Mismo patrón de búsqueda dinámica con debounce que ya usa
  // ManagerUnlinkedNotificationCertificate para buscar expedientes.
  useEffect(() => {
    if (!inputValue?.trim()) {
      setOptions([]);
      return undefined;
    }

    setLoading(true);

    const timeout = setTimeout(async () => {
      try {
        const res = await searchAvailableParties({
          search: inputValue.trim(),
          perPage: 15,
        });
        setOptions(res.data?.data || []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [inputValue]);

  const filteredOptions = options.filter((o) => !excludeIds.includes(o.id));

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        // Solo actualizamos por tipeo real o por el botón de limpiar.
        // Al seleccionar/quitar una opción MUI dispara este evento con otro
        // motivo ("reset"/"selectOption"/"removeOption") intentando vaciar
        // el texto: lo ignoramos para que la búsqueda y los resultados se
        // mantengan y se puedan tildar varias partes seguidas.
        if (reason === "input" || reason === "clear") {
          setInputValue(newInputValue);
        }
      }}
      options={filteredOptions}
      filterOptions={(x) => x}
      getOptionLabel={(option) =>
        `${option.name || ""} ${option.lastname || ""}`.trim() || `Parte #${option.id}`
      }
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={loading}
      noOptionsText={
        !inputValue?.trim() ? "Escribí para buscar" : "Sin resultados"
      }
      renderOption={(props, option, { selected }) => {
        const { key, ...rest } = props;
        return (
          <Box
            component="li"
            key={key}
            {...rest}
            sx={{
              display: "flex !important",
              alignItems: "flex-start !important",
              py: 0.75,
              gap: 0.5,
            }}
          >
            <Checkbox
              icon={uncheckedIcon}
              checkedIcon={checkedIcon}
              checked={selected}
              sx={{ p: 0.5, mt: 0.1 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.3 }}>
                {`${option.name || ""} ${option.lastname || ""}`.trim()}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.65 }}>
                {option.role || "—"} · {option.document || "sin documento"} ·{" "}
                {option.legal_files_count ?? 0} expediente(s)
                {option.client ? ` · vinculada a "${option.client.name}"` : ""}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...rest } = getTagProps({ index });
          return (
            <Chip
              key={key}
              {...rest}
              label={`${option.name || ""} ${option.lastname || ""}`.trim()}
              size="small"
              sx={{ fontWeight: 700 }}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value?.length ? "" : "Escribí para buscar…"}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <PersonSearchRoundedIcon sx={{ opacity: 0.4, ml: 0.5, mr: -0.5 }} fontSize="small" />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
